import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment-timezone";

import {
  TextAreaStyled,
  FormItemStyled,
  ValidationContainer,
  CharacterCount,
  EditContainer,
  EditNoteTextArea,
  NoteList,
  NoteItemContainer,
  ActionMenuWrapper,
  CreatedByLabel,
  NoteContent,
  ButtonContainerAddNote,
  AddNoteButton,
  CancelNoteButton,
  StyledEmptyText,
  HeaderContainer,
  NotesContainer,
  InputAreaContainer,
  ButtonsContainer,
  CancelEditNoteButton,
  NOTE_CHARACTER_LIMIT,
  FormItemStyledEditNote,
  EditNoteErrorContainer
} from "../../../sharedComponents/EditNotesUI";
import { Empty, Form, List, Modal, Space, Tooltip } from "antd";
import { GRAY_900, WHITE } from "../../../../constant/colors";
import { fontWeights, sizes } from "../../../../constant/styles";
import { ExpandableNoteContent } from "../PatientApplicationDetails/trackComponents/ExpandableNoteContent";
import { getNoteHeaderLabel, getNoteTooltipContent } from "../../../../utils/editNotes";
import { selectCurrentClaimDetails, selectUser } from "../../../../store/selector";
import { formatAssignee } from "../../../../utils/formaters";
import { ClaimNotesActionMenu } from "./ClaimNotesActionMenu";
import { ReactComponent as WarningIcon } from "../../../../assets/svg/warning-icon-popup.svg";

export const CLAIM_NOTE_TEXT_FIELD_NAME = "claimNoteText";

export const ClaimsNotesSection = ({ noteForm, notesList, setNotesList, setHasUnsavedNoteEdit }) => {
  const { t } = useTranslation();
  const { claimId } = useParams();

  const [modal, contextHolder] = Modal.useModal();
  const claimNoteText = Form.useWatch(CLAIM_NOTE_TEXT_FIELD_NAME, noteForm) || "";
  const currentClaimDetails = useSelector(selectCurrentClaimDetails(parseInt(claimId)));
  const currentUser = useSelector(selectUser);

  const [editingNoteInfo, setEditingNoteInfo] = useState({ id: null, note: "", hasUnsavedChanges: false, index: null });
  const [pendingNoteToEdit, setPendingNoteToEdit] = useState(null);

  const [hoveredNoteIndex, setHoveredNoteIndex] = useState(null);
  const [menuHoveredNoteIndex, setMenuHoveredNoteIndex] = useState(null);
  const [showUnsavedNoteEditChangesModal, setShowUnsavedNoteEditChangesModal] = useState(false);

  const disabledAddNote =
    (currentClaimDetails && !currentClaimDetails?.isActive) ||
    !claimNoteText.trim() ||
    claimNoteText.trim().length > NOTE_CHARACTER_LIMIT ||
    editingNoteInfo.hasUnsavedChanges;

  const disabledAddNoteTextArea =
    (currentClaimDetails && !currentClaimDetails?.isActive) || editingNoteInfo.hasUnsavedChanges;

  const disabledSaveEditNote =
    !editingNoteInfo.hasUnsavedChanges ||
    !editingNoteInfo.note.trim() ||
    editingNoteInfo.note.length > NOTE_CHARACTER_LIMIT;

  const getErrorMessage = () => {
    const errorMessage = noteForm.getFieldError(CLAIM_NOTE_TEXT_FIELD_NAME);
    return errorMessage?.[0] ? <div style={{ fontWeight: fontWeights.bold }}>{errorMessage[0]}</div> : null;
  };

  const getEditNoteErrorMessage = (index) => {
    const errorMessage = noteForm.getFieldError(`editedNoteText-${index}`);
    return errorMessage?.[0] ? errorMessage[0] : null;
  };

  const renderFormErrorMessage = () => getErrorMessage();
  const hasAddNoteError = !!getErrorMessage();

  const handleAddNote = () => {
    if (!claimNoteText || !claimNoteText.trim()) {
      return;
    }

    setNotesList([
      {
        note: claimNoteText.trim(),
        createdAt: moment(),
        updatedAt: moment(),
        createdByUserName: formatAssignee(currentUser),
        updatedByUserName: null,
        isNew: true,
        userUpdatedAt: null
      },
      ...notesList
    ]);

    noteForm.setFieldsValue({
      [CLAIM_NOTE_TEXT_FIELD_NAME]: ""
    });
  };

  const handleNoteChange = (e) => {
    const value = e.target.value;

    if (value.length > NOTE_CHARACTER_LIMIT) {
      noteForm.setFields([
        {
          name: CLAIM_NOTE_TEXT_FIELD_NAME,
          value: value,
          errors: [t("patientApplications.claims_tab.error_messages.notes_length_error")]
        }
      ]);
    } else {
      noteForm.setFields([
        {
          name: CLAIM_NOTE_TEXT_FIELD_NAME,
          value: value,
          errors: []
        }
      ]);
    }
  };

  const clearHoveredNoteId = () => {
    setHoveredNoteIndex(null);
    setMenuHoveredNoteIndex(null);
  };

  const handleMenuHoverChange = (noteIndex, isHovered) => {
    setMenuHoveredNoteIndex(isHovered ? noteIndex : null);
  };

  const getOriginalNoteContent = () => {
    const originalNote = notesList.find((note, index) => index === editingNoteInfo.index);
    return originalNote ? originalNote.note : "";
  };

  const handleEditNoteTextChange = (e) => {
    const newText = e.target.value;
    const originalContent = getOriginalNoteContent(editingNoteInfo.index);
    const hasChanges = newText.trim() !== originalContent.trim();

    setEditingNoteInfo({
      ...editingNoteInfo,
      note: newText,
      hasUnsavedChanges: hasChanges
    });

    // Trigger validation for the edit note field
    const fieldName = `editedNoteText-${editingNoteInfo.index}`;
    if (newText.length > NOTE_CHARACTER_LIMIT) {
      noteForm.setFields([
        {
          name: fieldName,
          value: newText,
          errors: [t("patientApplications.claims_tab.error_messages.notes_length_error")]
        }
      ]);
    } else {
      noteForm.setFields([
        {
          name: fieldName,
          value: newText,
          errors: []
        }
      ]);
    }
  };

  const handleEditNote = (note) => {
    if (editingNoteInfo.index >= 0 && editingNoteInfo.hasUnsavedChanges && editingNoteInfo.index !== note.index) {
      setPendingNoteToEdit(note);
      setShowUnsavedNoteEditChangesModal(true);
      return;
    }

    setEditingNoteInfo({ id: note?.id || null, note: note.note, hasUnsavedChanges: false, index: note.index });
    noteForm.setFieldValue(`editedNoteText-${note.index}`, note.note);
  };

  const handleSaveEdit = () => {
    const fieldName = `editedNoteText-${editingNoteInfo.index}`;
    const noteText = noteForm.getFieldValue(fieldName);

    const updateNotes = [...notesList].map((note, index) => {
      if (index === editingNoteInfo.index) {
        note.note = noteText;
        note.updatedAt = moment();
        note.updatedByUserName = formatAssignee(currentUser);
        note.isModified = true;
        note.userUpdatedAt = moment();
      }
      return note;
    });

    setNotesList(updateNotes);
    setEditingNoteInfo({ id: null, note: "", hasUnsavedChanges: false, index: null });
    clearHoveredNoteId();
  };

  const handleCancelEdit = () => {
    if (editingNoteInfo.index >= 0) {
      noteForm.resetFields([`editedNoteText-${editingNoteInfo.index}`]);
    }
    setEditingNoteInfo({ id: null, note: "", hasUnsavedChanges: false, index: null });
    setPendingNoteToEdit(null);
    clearHoveredNoteId();
  };

  const handleContinueAfterEditNoteAction = () => {
    if (pendingNoteToEdit) {
      setEditingNoteInfo({
        id: pendingNoteToEdit?.id,
        note: pendingNoteToEdit.note,
        hasUnsavedChanges: false,
        index: pendingNoteToEdit.index
      });
      noteForm.setFieldValue(`editedNoteText-${pendingNoteToEdit.index}`, pendingNoteToEdit.note);
    } else {
      setEditingNoteInfo({ id: null, note: "", hasUnsavedChanges: false, index: null });
    }

    setShowUnsavedNoteEditChangesModal(false);
    setPendingNoteToEdit(null);
    clearHoveredNoteId();
  };

  const handleReturnToEditMode = () => {
    setShowUnsavedNoteEditChangesModal(false);
    setPendingNoteToEdit(null);
    clearHoveredNoteId();
  };

  const handleDiscardEditNoteAndContinue = () => {
    handleContinueAfterEditNoteAction();
  };

  useEffect(() => {
    if (showUnsavedNoteEditChangesModal) {
      modal.confirm({
        centered: true,
        okText: t("yes_sure"),
        cancelText: t("back"),
        icon: <WarningIcon />,
        title: t("pop_up_messages.unsaved_changes"),
        content: <>{t("pop_up_messages.you_have_unsaved_changes_in_your_note")}</>,
        onOk: handleDiscardEditNoteAndContinue,
        onCancel: handleReturnToEditMode,
        okButtonProps: {
          id: "yes_sure_button"
        },
        cancelButtonProps: {
          id: "back_button"
        }
      });
    }
  }, [showUnsavedNoteEditChangesModal, pendingNoteToEdit]);

  useEffect(() => {
    setHasUnsavedNoteEdit(editingNoteInfo.hasUnsavedChanges);
  }, [editingNoteInfo.hasUnsavedChanges]);

  return (
    <NotesContainer style={{ borderRight: "0px", borderBottom: "0px" }} id="claims_notes_section">
      <HeaderContainer>{t("patientApplications.claims_tab.notes_section.notes")}</HeaderContainer>
      <InputAreaContainer>
        <Form form={noteForm} layout="vertical">
          <FormItemStyled
            name={CLAIM_NOTE_TEXT_FIELD_NAME}
            validateTrigger={["onChange"]}
            rules={[
              {
                max: NOTE_CHARACTER_LIMIT,
                message: t("patientApplications.claims_tab.error_messages.notes_length_error")
              }
            ]}
          >
            <TextAreaStyled
              placeholder={t("patientApplications.claims_tab.notes_section.add_a_new_note")}
              showCount
              disabled={disabledAddNoteTextArea}
              onChange={handleNoteChange}
            />
          </FormItemStyled>

          <ValidationContainer hasError={hasAddNoteError}>
            <CharacterCount hasError={hasAddNoteError && claimNoteText?.length > NOTE_CHARACTER_LIMIT}>
              {claimNoteText?.length || 0}/{NOTE_CHARACTER_LIMIT}
            </CharacterCount>
            <Form.Item shouldUpdate noStyle>
              {renderFormErrorMessage}
            </Form.Item>
          </ValidationContainer>

          <ButtonContainerAddNote>
            <CancelNoteButton
              type="link"
              onClick={() => {
                noteForm.resetFields();
              }}
              disabled={!claimNoteText.trim()}
              id="cancel_note_button"
            >
              {t("patientApplications.claims_tab.notes_section.cancel")}
            </CancelNoteButton>

            <AddNoteButton type="primary" onClick={handleAddNote} disabled={disabledAddNote} id="save_note_button">
              {t("patientApplications.claims_tab.notes_section.save_note")}
            </AddNoteButton>
          </ButtonContainerAddNote>
        </Form>
      </InputAreaContainer>

      <NoteList
        dataSource={notesList}
        locale={{
          emptyText: (
            <Empty
              description={
                <Space direction="vertical" size={0}>
                  <StyledEmptyText>{t("patientApplications.claims_tab.notes_section.no_notes_yet")}</StyledEmptyText>
                  <StyledEmptyText>
                    {t("patientApplications.claims_tab.notes_section.start_by_typing_in_the_box_above")}
                  </StyledEmptyText>
                </Space>
              }
            />
          )
        }}
        renderItem={(note, index) => {
          const { createdByUserName, updatedByUserName, createdAt, userUpdatedAt } = note || {};
          const isEditing = editingNoteInfo?.index === index;

          const noteHeaderLabel = getNoteHeaderLabel({
            t,
            updatedByUserName,
            createdByUserName,
            updatedAt: userUpdatedAt,
            createdAt
          });
          const noteTooltipContent = getNoteTooltipContent({
            t,
            updatedByUserName,
            createdByUserName,
            createdAt,
            updatedAt: userUpdatedAt
          });
          const hasEditNoteError = !!getEditNoteErrorMessage(index);

          if (note?.markAsDeleted) return null;

          return (
            <List.Item
              key={index}
              onMouseEnter={() => setHoveredNoteIndex(index)}
              onMouseLeave={() => {
                if (menuHoveredNoteIndex !== index) {
                  setHoveredNoteIndex(null);
                }
              }}
            >
              <List.Item.Meta
                key={index}
                id={note.id}
                description={
                  <NoteItemContainer isEditing={isEditing}>
                    <Space direction="vertical" size={isEditing ? 5 : 0} style={{ width: "100%" }}>
                      <Tooltip
                        title={noteTooltipContent}
                        id="note_header_tooltip"
                        placement="top"
                        overlayInnerStyle={{
                          backgroundColor: GRAY_900,
                          borderRadius: "8px",
                          fontWeight: fontWeights.semibold,
                          fontSize: sizes.small,
                          color: WHITE
                        }}
                        color={GRAY_900}
                        overlayStyle={{
                          maxWidth: "400px",
                          whiteSpace: "pre-line"
                        }}
                      >
                        <CreatedByLabel id="note_header_label">{noteHeaderLabel}</CreatedByLabel>
                      </Tooltip>
                      {isEditing ? (
                        <Form form={noteForm} layout="vertical">
                          <EditContainer>
                            <FormItemStyledEditNote
                              name={`editedNoteText-${index}`}
                              initialValue={note.note}
                              rules={[
                                {
                                  max: NOTE_CHARACTER_LIMIT,
                                  message: t("patientApplications.claims_tab.error_messages.notes_length_error")
                                }
                              ]}
                            >
                              <EditNoteTextArea
                                onChange={handleEditNoteTextChange}
                                showCount
                                autoSize={false}
                                placeholder={t("patientApplications.claims_tab.notes_section.edit_note_placeholder")}
                                id="edit_note_text_area"
                                hasError={hasEditNoteError}
                              />
                            </FormItemStyledEditNote>

                            <ButtonsContainer>
                              <EditNoteErrorContainer hasError={hasEditNoteError}>
                                <CharacterCount hasError={hasEditNoteError}>
                                  {editingNoteInfo.note?.length || 0}/{NOTE_CHARACTER_LIMIT}
                                </CharacterCount>
                                <div>{getEditNoteErrorMessage(index)}</div>
                              </EditNoteErrorContainer>

                              <div style={{ gap: "8px", display: "flex", flexDirection: "column" }}>
                                <AddNoteButton
                                  type="primary"
                                  onClick={handleSaveEdit}
                                  disabled={disabledSaveEditNote}
                                  isEditing={true}
                                  id="save_note_button"
                                >
                                  {t("patientApplications.claims_tab.notes_section.save_note")}
                                </AddNoteButton>
                                <CancelEditNoteButton type="link" onClick={handleCancelEdit} id="cancel_note_button">
                                  {t("patientApplications.claims_tab.notes_section.cancel")}
                                </CancelEditNoteButton>
                              </div>
                            </ButtonsContainer>
                          </EditContainer>
                        </Form>
                      ) : (
                        <NoteContent id="note_content">
                          <ExpandableNoteContent>{note.note}</ExpandableNoteContent>
                        </NoteContent>
                      )}
                    </Space>
                    {!isEditing && (
                      <ActionMenuWrapper
                        className="note-action-menu"
                        isVisible={hoveredNoteIndex === index || menuHoveredNoteIndex === index}
                      >
                        <ClaimNotesActionMenu
                          note={note}
                          notesList={notesList}
                          setNotesList={setNotesList}
                          noteIndex={index}
                          isVisible={hoveredNoteIndex === index || menuHoveredNoteIndex === index}
                          onMenuHoverChange={(isHovered) => handleMenuHoverChange(index, isHovered)}
                          onEditNote={handleEditNote}
                          setHoveredNoteIndex={setHoveredNoteIndex}
                        />
                      </ActionMenuWrapper>
                    )}
                  </NoteItemContainer>
                }
              />
            </List.Item>
          );
        }}
      />
      {contextHolder}
    </NotesContainer>
  );
};
